// Icon, Icon Tile and Separator: the small pieces other components are built from.
import { I80Element, define, h, lateLabel } from "../base.js";
import { icon } from "../icons.js";

const COLORS = ["primary", "strong", "faint", "disabled", "action", "action-hover", "action-active",
  "high-contrast", "success", "warning", "critical", "focus-action-internal", "focus-action-external"];

/**
 * <i80-icon name="plus"></i80-icon>
 * <i80-icon name="alert-triangle" size="24" color="warning" title="Check this"></i80-icon>
 *
 * A Flight icon. Without a title it is decorative and hidden from screen readers; with one
 * it is an image with that name. The file is fetched from icons/ next to i80.js.
 */
export class I80Icon extends I80Element {
  static get observedAttributes() {
    return ["name", "size", "color", "title", "stretched", "inline"];
  }

  setup() {
    this._svg = this.part("icon");
  }

  update() {
    const name = this.getAttribute("name");
    if (!name) {
      console.warn("<i80-icon> needs a name, e.g. <i80-icon name=\"plus\">.");
      return;
    }
    const size = this.choice("size", ["16", "24"], "16");
    const title = this.getAttribute("title");
    const color = this.getAttribute("color");
    const classes = [
      this.flag("inline") && "i80-icon--is-inline",
      COLORS.includes(color) && `i80-foreground-${color}`,
    ].filter(Boolean).join(" ");

    const svg = icon(name, { size, stretched: this.flag("stretched"), className: classes, title });
    svg.setAttribute("data-i80-part", "icon");
    if (color && !COLORS.includes(color)) svg.style.color = color;
    this._svg?.remove();
    this._svg = svg;
    this.append(svg);
  }
}

/**
 * <i80-icon-tile icon="folder" color="brand"></i80-icon-tile>
 * A rounded tile with an icon in it, used to label a page or a row.
 */
export class I80IconTile extends I80Element {
  static get observedAttributes() {
    return ["icon", "size", "color"];
  }

  setup() {
    this._tile = this.part("tile") ?? h("div", { "data-i80-part": "tile" });
    if (!this._tile.isConnected) this.append(this._tile);
  }

  update() {
    const size = this.choice("size", ["small", "medium", "large"], "medium");
    const color = this.choice("color", ["neutral", "brand", "coral", "pink", "green", "sky", "purple", "blue", "yellow", "teal"], "neutral");
    this._tile.className = `i80-icon-tile i80-icon-tile--icon i80-icon-tile--size-${size} i80-icon-tile--color-${color}`;
    this._tile.setAttribute("aria-hidden", "true");
    const name = this.getAttribute("icon");
    this._tile.replaceChildren(...(name
      ? [h("div", { class: "i80-icon-tile__icon" }, icon(name, { size: size === "small" ? "16" : "24", stretched: true }))]
      : []));
  }
}

/**
 * <i80-separator></i80-separator>
 * A line between groups of content. `spacing="0"` removes the margin around it.
 */
export class I80Separator extends I80Element {
  static get observedAttributes() {
    return ["spacing"];
  }

  setup() {
    this._hr = this.part("separator") ?? h("hr", { "data-i80-part": "separator" });
    if (!this._hr.isConnected) this.append(this._hr);
  }

  update() {
    this._hr.className = `i80-separator i80-separator--spacing-${this.choice("spacing", ["24", "0"], "24")}`;
  }
}

/**
 * <i80-badge-count text="12"></i80-badge-count>
 * A number next to a label: unread items, results, tasks.
 */
export class I80BadgeCount extends I80Element {
  static get observedAttributes() {
    return ["text", "size", "type", "color"];
  }

  setup() {
    this._box = this.part("box") ?? h("div", { "data-i80-part": "box" }, ...this.takeContent());
    if (!this._box.isConnected) this.append(this._box);
    this._label = this.getAttribute("text") ?? this._box.textContent.trim();
  }

  adopt() {
    const label = lateLabel(this);
    if (label) this._label = label;
  }

  update() {
    this._box.className = [
      "i80-badge-count",
      `i80-badge-count--size-${this.choice("size", ["small", "medium"], "medium")}`,
      `i80-badge-count--type-${this.choice("type", ["filled", "inverted", "outlined"], "filled")}`,
      `i80-badge-count--color-${this.choice("color", ["neutral", "neutral-dark-mode"], "neutral")}`,
    ].join(" ");
    this._box.textContent = this.getAttribute("text") ?? this._label;
  }
}

/**
 * <i80-button-set>…buttons…</i80-button-set>
 * Buttons in a row with the right spacing; a tertiary button is pushed to the end.
 */
export class I80ButtonSet extends I80Element {
  setup() {
    this._box = this.part("set") ?? h("div", { class: "i80-button-set", "data-i80-part": "set" }, this.takeContent());
    if (!this._box.isConnected) this.append(this._box);
  }

  adopt() {
    this._box.append(...this.takeContent());
  }

  update() {
    this._box.className = "i80-button-set";
  }
}

define("i80-icon", I80Icon);
define("i80-icon-tile", I80IconTile);
define("i80-separator", I80Separator);
define("i80-badge-count", I80BadgeCount);
define("i80-button-set", I80ButtonSet);
